//import EventStream from "Models/EventStream";
import { EventStreamPhaser2 } from 'Phaser/GameObjects/CursorMobile';
import { IteratorExtensible } from 'Phaser/GameObjects/CursorMobile';
import Phaser from 'phaser';
import { ErrorFeedbackManager } from 'Phaser/GameObjects/ErrorFeedbackManager';
import {
  AppTourStepPhaser,
  ReactToPhaserListeners,
  NotifyReactEvents,
  RemoveListener,
} from 'Phaser/EventBus2';
import CursorMobile from 'Phaser/GameObjects/CursorMobile';
import IPlayable from 'Phaser/GameObjects/Playable';
import {
  DefaultConfig,
  getPhraseEnd,
  MediumConfig,
  MinimumConfig,
  PhraseData,
  PhraseGraphic,
} from 'Phaser/GameObjects/StaffLine';
import { NoteGraphic } from 'Phaser/GameObjects/StaffLine/Note';
import { PrerenderedPhraseData } from 'Types';
import { INoteGraphicsDatabase } from './StaffExercise';
import { ProgressState } from 'Phaser/GameObjects/Progress';
import Phrase, { Iterator } from 'Models/Phrase';
import ITimeKeeper from 'Models/ITimeKeeper';
import TimeKeeper from 'Models/TimeKeeper';
import { createTimeKeeper } from 'Models/createTimeKeeper';
import * as Tone from 'tone';
import Logger from 'Utils/Logger';
import { NoteType } from '@museflow-ai/opensheetmusicdisplay';
import TimeSignature from 'Models/TimeSignature';
import ChevronManager from 'Phaser/Gameplay/ChevronManager';
import { COLORBLIND_TYPE, isCapacitorNative } from 'Utils/Constants';
import { getBraceWidth, optimizeStaffLineHeight } from 'Phaser/GameObjects/StaffLine/Util';
import { SizingProps } from 'Phaser/GameObjects/StaffLine/Types';
import { FeatureFlag, isFeatureEnabled } from 'Utils/FeatureFlags';
import { AdaptiveTempoController } from 'Phaser/Gameplay/AdaptiveTempoController';

const AUTOSTART = false;

// limit the value to the nearest step (0.05)
function _limitValueToStep(value: number, step: number) {
  return Math.floor(value / step) * step;
}

export class PlayablePhrase {
  cursor?: CursorMobile;
  graphic: PhraseGraphic;
  idx: number = 0;
  isActive: boolean = false;

  constructor(graphic: PhraseGraphic, cursor?: CursorMobile) {
    this.cursor = cursor;
    this.graphic = graphic;
  }

  activate(unpause?: boolean, opaque = true) {
    this.isActive = true;
    this.cursor?.enable(opaque);
    //this.cursor?.setAlpha();
    if (unpause) this.cursor?.unpause();
  }

  deactivate() {
    this.isActive = false;
    this.cursor?.disable();
  }

  pause() {
    this.cursor?.pause();
  }

  update() {
    this.graphic.update();
    if (this.isActive) this.cursor?.update();
  }

  destroy() {
    this.graphic.destroy();
    this.cursor?.destroy();
  }
}

export default class SightReadingPhaser extends Phaser.Scene implements INoteGraphicsDatabase {
  phrases: (PlayablePhrase | undefined)[] = [];
  mainIterator!: IteratorExtensible;
  //cursors: CursorMobile[] = [];
  currentCursor: number = 0;
  currentIdx?: number;
  totalPhrases: number = 0;
  eventStream?: EventStreamPhaser2;
  log?: Phaser.GameObjects.Text;

  timeKeeperAuxiliary?: ITimeKeeper;

  phrasesSeen: Map<string, number> = new Map();

  clickAudio?: Phaser.Sound.BaseSound;
  tempo: number = 72;

  lastTickTime?: number;
  lastMeasure?: number;

  // This is a hack for maintaining the correct sequence when new phrases get loaded in
  // should no longer be necessary once we get all the data baked in from the backend
  phraseLoadQueue: {
    phrase: PhraseData;
    cursorData: PrerenderedPhraseData[];
    phraseRaw: Phrase;
  }[] = [];

  pauseQueued = false;
  playbackStarted = false;
  shouldUpdate = false;

  measureCount: number = 0;

  mobileView: boolean = false;
  width: number = 0;
  height: number = 0;
  private firstPhrase = true;

  topPhrase: number = 0;
  bottomPhrase: number = 1;

  private activePhrase = 0;
  zoomTween?: Phaser.Tweens.Tween;
  mobileCursor: CursorMobile | undefined;
  originMarker: any;

  getActivePhrase(): number {
    return this.activePhrase;
  }
  private phrasesTillNextTier = 4;
  private freePlayMode: boolean = false;

  private gameplayStarted: boolean = false;

  private eventsQueue: [number, number][] = [];
  private startPlayingTriggerEventTimestamp?: number;

  private nextPhraseStartTimer?: Phaser.Time.TimerEvent;

  private chevronManager?: ChevronManager;
  private errorFeedbackManager?: ErrorFeedbackManager;
  private phrase1Gizmo?: Phaser.GameObjects.Rectangle;
  private phrase2Gizmo?: Phaser.GameObjects.Rectangle;

  private maxZoomLevel: number = 1;
  private zoomLevel: number = 1;
  private cursorHidden: boolean = false;

  /** App tour: current step (null when not in tour). */
  private appTourStep: AppTourStepPhaser | null = null;
  /** App tour: true while playback demo (cursor + metronome) is running. */
  private appTourPlaybackActive: boolean = false;
  /** App tour: snapshot to restore on EndTour. */
  private savedTourState?: {
    activePhrase: number;
    timeKeeperPaused: boolean;
    lastTickTime: number | undefined;
  };
  /** App tour: demo timekeeper for playback step (72 BPM). */
  private demoTimeKeeper?: TimeKeeper;
  /** App tour: demo iterator reference (for EndReached check). */
  private appTourDemoIterator?: IteratorExtensible;
  /** App tour: note IDs in order by slice (notes only, no rests) for coloring steps. */
  private appTourNoteIdsBySlice: string[] = [];
  /** App tour: current coloring step index (0=Perfect, 1=OffTime, 2=Error). */
  private appTourColoringStepIndex: number = 0;
  /** App tour: note IDs that had setDemoTimingState called (for restore on EndTour). */

  private appTourDemoTouchedNoteIds: Set<string> = new Set();
  /** Phrase raw (Phrase) by uuid for building demo iterator. */
  private phraseRawByUuid: Map<string, Phrase> = new Map();

  private isTourActive: boolean = false;
  private savedCursorPosition?: number;

  private notePosGizmo?: Phaser.GameObjects.Rectangle;

  private adaptiveTempoController?: AdaptiveTempoController;

  private nextAdaptiveSegment?: {
    base: number;
    delta: number;
    startTimestamp: number;
    endTimestamp: number;
  };

  public debugText?: Phaser.GameObjects.Text;

  constructor() {
    super('SightReadingPhaser');
  }

  create() {
    NotifyReactEvents.CurrentSceneReady(this);
    this.events.once('shutdown', this.destroy, this);
  }

  debugLog(text: string) {
    if (process.env.REACT_APP_NODE_ENV === 'prd' || process.env.REACT_APP_NODE_ENV === 'stg')
      return;
    //Logger.debug(text);
    this.debugText?.setText(text);
  }

  init({
    eventStream,
    mobile,
    width,
    height,
    zoomLevel,
    goalTempo = 72,
  }: {
    eventStream: EventStreamPhaser2;
    mobile?: boolean;
    width: number;
    height: number;
    zoomLevel: number;
    goalTempo?: number;
  }) {
    this.debugText =
      process.env.REACT_APP_NODE_ENV !== 'prd' && process.env.REACT_APP_NODE_ENV !== 'stg'
        ? this.add.text(100, 100, 'DebugLog', { fontSize: '32px', color: '#000000' })
        : undefined;
    this.adaptiveTempoController = new AdaptiveTempoController(
      this.eventStream?.timeKeeper.getBpm() ?? 72,
      this.onAdaptiveTempoDone,
      this,
      72,
      this.freePlayMode,
      40,
      300,
    );
    Logger.debug('[zoom::init] loading scene with ', zoomLevel);
    this.setZoomLevel({ zoomLevel: zoomLevel ?? 0.9 });
    /*if (zoomLevel) {
      this.zoomLevel = Math.max(0.5, Math.min(zoomLevel, 1));
      this.cameras.main.zoom = this.zoomLevel;
    } else {
      this.zoomLevel = 0.9;
      this.cameras.main.zoom = this.zoomLevel;
    }*/
    //console.debug(this.cameras.main);
    //console.debug('[zoom::init] loading scene with ', zoomLevel, this.cameras.main.zoom);
    // Reset all per-session state — the scene instance is reused across
    // tier changes and re-starts, so class property initializers do NOT run
    // again. Every mutable field must be explicitly reset here.
    this.phrases = [];
    this.currentIdx = undefined;
    this.currentCursor = 0;
    this.measureCount = 0;
    this.activePhrase = 0;
    this.phrasesSeen = new Map();
    this.phraseLoadQueue = [];
    this.phraseRawByUuid = new Map();
    this.lastTickTime = undefined;
    this.lastMeasure = undefined;
    this.topPhrase = 0;
    this.bottomPhrase = 1;
    this.firstPhrase = true;
    this.playbackStarted = false;
    this.shouldUpdate = false;
    this.pauseQueued = false;
    this.gameplayStarted = false;
    this.eventsQueue = [];
    this.freePlayMode = false;
    this.nextPhraseStartTimer = undefined;
    this.startPlayingTriggerEventTimestamp = undefined;
    this.appTourStep = null;
    this.appTourPlaybackActive = false;
    this.savedTourState = undefined;
    this.demoTimeKeeper = undefined;
    this.appTourDemoIterator = undefined;
    this.appTourNoteIdsBySlice = [];
    this.appTourColoringStepIndex = 0;
    this.appTourDemoTouchedNoteIds = new Set();
    this.isTourActive = false;
    this.savedCursorPosition = undefined;

    this.chevronManager = new ChevronManager(
      eventStream,
      this.tempo,
      72,
      4,
      {
        callback: this.onGainChevron,
        context: this,
      },
      {
        callback: this.onLoseChevron,
        context: this,
      },
      this.freePlayMode,
    );
    this.errorFeedbackManager = new ErrorFeedbackManager(this);
    this.width = width;
    this.height = height;
    this.mobileView = mobile ?? false;
    this.timeKeeperAuxiliary = createTimeKeeper();
    this.eventStream = eventStream;
    this.eventStream.setErrorFeedbackManager(this.errorFeedbackManager, this);
    this.phrases = [];
    this.cameras.main.setBackgroundColor('0xffffff');
    // Default camera zoom origin is (0.5, 0.5): zoom scales around viewport center, so world (0,0)
    // is generally not the same as the top-left of what you see unless zoom is 1 and layout lines up.
    // For the world point at the viewport's top-left: getWorldPoint(0, 0) or worldView.x / worldView.y.
    //this.log = this.add.text(0, 0, "log");

    ReactToPhaserListeners.ResizeWindow(this.resize, this);
    ReactToPhaserListeners.PauseExercise(this.pauseExercise, this);
    ReactToPhaserListeners.AddPhraseLegacy(this.addPhrase, this, false, true);
    ReactToPhaserListeners.MidiOn(this.noteOn, this);
    ReactToPhaserListeners.MidiOff(this.noteOff, this);
    ReactToPhaserListeners.SetZoomLevel(this.setZoomLevel, this);
    ReactToPhaserListeners.SetNoteState(this.setNoteState, this, false, false);
    ReactToPhaserListeners.PulseNote(this.pulseNote, this, false, false);
    ReactToPhaserListeners.SetNoteState(this.setNoteState, this, false, false);
    ReactToPhaserListeners.StartExercise(this.startExercise, this, false, false);
    ReactToPhaserListeners.ChangeTempo(this.changeTempo, this, false, false);
    ReactToPhaserListeners.UpdateGoalTempo(this.updateGoalTempo, this, false, true);
    //ReactToPhaserListeners.AddPhrase(this.addPhrase, this, false, true);
    ReactToPhaserListeners.SetCursor(this.setCursor, this);
    ReactToPhaserListeners.SetFreePlayMode(this.setFreePlayMode, this, false, true);
    ReactToPhaserListeners.ResetNoteColor(this.resetNoteColor, this);
    ReactToPhaserListeners.ResetChevrons(() => {
      this.chevronManager?.reset();
    }, this);
    ReactToPhaserListeners.SetNoteColor(this.setNoteColor, this);
    ReactToPhaserListeners.SetAppTourStep(this.setAppTourStep, this);
    ReactToPhaserListeners.EndTour(this.endTour, this);
    ReactToPhaserListeners.SetCursorVisible(this.setCursorVisible, this);
    if (!this.clickAudio) {
      this.clickAudio = this.sound.add('click');
    }
    ReactToPhaserListeners.AbortAdaptiveTempo(this.abortAdaptiveSegment, this);
    /*this.phrase1Gizmo = this.add.rectangle(0, 0, 100, 100, 0x00ff00, 0.5);
    this.phrase2Gizmo = this.add.rectangle(0, 0, 100, 100, 0x00ff00, 0.5);
    this.phrase1Gizmo?.setOrigin(0, 0);
    this.phrase2Gizmo?.setOrigin(0, 0);*/
  }

  abortAdaptiveSegment({ tempoRestorePoint }: { tempoRestorePoint?: number }) {
    this.adaptiveTempoController?.abortAdaptiveSegment({ tempoRestorePoint });
    this.nextAdaptiveSegment = undefined;
  }

  updateMaxZoomLevel(paddingHorizontal: number, paddingVertical: number, braceWidth: number) {
    if (!isFeatureEnabled(FeatureFlag.USE_LESSON_ZOOM)) return;
    const cam = this.cameras.main;
    const vpWidth = cam.width;
    const vpHeight = cam.height;
    const extra = 5 * window.devicePixelRatio;
    const phraseWidth = vpWidth - (paddingHorizontal - braceWidth - extra) * 2;
    const phraseHeight = vpHeight - (paddingVertical - extra) * 2;
    const maxZoomLevel = this.mobileView
      ? vpHeight / phraseHeight
      : Math.min(vpWidth / phraseWidth, vpHeight / phraseHeight);
    this.maxZoomLevel = maxZoomLevel;
    this.setZoomLevel({ zoomLevel: this.zoomLevel });
  }

  setZoomLevel({ zoomLevel }: { zoomLevel: number }) {
    //if (this.mobileView) return;
    Logger.debug('[zoom::setZoomLevel] setting zoom level', zoomLevel);
    if (zoomLevel === undefined) {
      Logger.warn('[zoom::setZoomLevel] zoomLevel is undefined!');
      return;
    }
    if (!isFeatureEnabled(FeatureFlag.USE_LESSON_ZOOM)) return;
    const zoom = Math.max(0.5, Math.min(zoomLevel, 1));
    this.zoomLevel = zoom;
    this.zoomTween?.stop();
    this.zoomTween = this.tweens.add({
      targets: this.cameras.main,
      zoom: zoom * this.maxZoomLevel,
      duration: 500,
      ease: 'Power2',
      onComplete: () => {
        Logger.debug(
          'camera zoom after setZoomLevel complete',
          this.cameras.main.zoom,
          this.maxZoomLevel,
        );
      },
    });
    Logger.debug('camera zoom after setZoomLevel', this.cameras.main.zoom);
  }

  setFreePlayMode({ enabled }: { enabled: boolean }) {
    console.debug('[collected message] setFreePlayMode', enabled);
    this.adaptiveTempoController?.setIsFreePlay(enabled);
    this.freePlayMode = enabled;
    this.chevronManager?.setFreePlayMode(enabled);
  }

  startExerciseDummy() {
    NotifyReactEvents.StartedPlaying();
  }

  setCursor(_props: { measure?: number }) {
    if (!this.playbackStarted) {
      return;
    }
    const cursor = this.mobileCursor ?? this.phrases[this.activePhrase]?.cursor;
    const measure = Math.max(cursor!.getLastPlayedMeasure() - 1, 0);
    if (measure === -1) return;
    const phraseIdx = this.phrases.findIndex((phrase) => phrase?.graphic.containsMeasure(measure));
    if (phraseIdx === undefined) {
      console.error("given measure doesn't appear in any of the active phrases!");
    } else {
      const phrase = this.phrases[phraseIdx];
      if (phrase !== this.phrases[this.activePhrase]) {
        console.debug(
          '[setCursor] phrase changed',
          phraseIdx,
          this.activePhrase,
          this.phrases,
          this.activePhrase,
        );
        const current = this.phrases[this.activePhrase]!;
        const firstNotePos = this.phrases[this.activePhrase]!.graphic.getFirstNotePosition();
        const cursor = this.mobileCursor ?? this.phrases[this.activePhrase]?.cursor;
        cursor?.setStart(firstNotePos, current.graphic._data.startTimestamp, 0);
        cursor?.setX(firstNotePos - cursor!.width * 2);
        current?.deactivate();
        this.activePhrase = phraseIdx;
      }
      phrase?.activate();
      const firstNote =
        phrase?.graphic.getFirstNotePositionInMeasure(
          measure - phrase.graphic.phraseStartingMeasure!,
        ) ?? 0;
      const cursorStartPos = firstNote!;
      const numMeasuresIn = measure - (phrase?.graphic.phraseStartingMeasure ?? 0);
      const timesig = phrase?.graphic._data.timeSignature ?? new TimeSignature(4, 4);
      const cursor = (this.mobileCursor ?? this.phrases[this.activePhrase]?.cursor)!;
      cursor.lastPlayedMeasure = measure - cursor.startingMeasure;
      cursor.setStart(
        cursorStartPos,
        (timesig.numerator / timesig.denominator) * numMeasuresIn,
        measure - cursor.startingMeasure,
      );
    }
  }

  changeTempo({ tempo }: { tempo: number }) {
    this.tempo = tempo;
    this.timeKeeperAuxiliary!.setBpm(tempo);

    // If waiting for the first note (cursor blinking), update blink speed
    // to match the new tempo so the user sees the change immediately.
    if (!this.playbackStarted) {
      const activePhrase = this.phrases[this.activePhrase];
      const cursor = this.mobileCursor ?? activePhrase?.cursor;
      cursor?.blink((60 / tempo) * 1000);
    }
  }

  setCursorVisible = ({ visible }: { visible: boolean }): void => {
    this.cursorHidden = !visible;
    for (const phrase of this.phrases) {
      phrase?.cursor?.setRenderHidden(!visible);
    }
  };

  metronomeClick() {
    if (this.clickAudio === undefined) {
      Logger.warn('metronome audio not loaded!');
    }
    this.clickAudio?.stop();
    this.clickAudio?.play();
  }

  startExercise({ tempo }: { tempo?: number }) {
    if (tempo) {
      this.tempo = tempo;
    }
    if (AUTOSTART && process.env.REACT_APP_NODE_ENV === 'local') {
      setTimeout(() => this.noteOn({ note: 60, velocity: 1 }), 500);
    }
    this.shouldUpdate = true;
    if (!this.playbackStarted) {
      const secondsPerMeasure = (60 / this.tempo) * 4;
      const measureOffset = TimeKeeper.METRONOME_SCHEDULE_AHEAD_TIME / secondsPerMeasure;
      if (!isCapacitorNative()) {
        Tone.start();
      }
      this.timeKeeperAuxiliary!.setTimeSignatures(0, [new TimeSignature(4, 4)]);
      this.timeKeeperAuxiliary!.resetTime();
      this.timeKeeperAuxiliary!.unpause(this.tempo, 0, measureOffset, NoteType._32nd);
      this.phrases[0]!.activate();
      this.mobileCursor?.enable();
      this.mobileCursor?.unpause();
      const cursor = this.mobileCursor ?? this.phrases[0]!.cursor;
      console.debug('[startExercise] cursor=', cursor);
      cursor?.blink((60 / this.tempo) * 1000);
    } else {
      console.log('[startExercise] === RESUME START ===');
      console.log(
        '[startExercise] activePhrase=',
        this.activePhrase,
        'phraseLoadQueue.length=',
        this.phraseLoadQueue.length,
        'currentIdx=',
        this.currentIdx,
      );
      console.log(
        '[startExercise] phrase slots:',
        this.phrases.map((p, i) =>
          p
            ? `[${i}] uuid=${p.graphic._data.uuid} ts=${p.graphic._data.startTimestamp} active=${p.isActive} destroyed=${p.graphic.isDestroyed}`
            : `[${i}] empty`,
        ),
      );
      console.log(
        '[startExercise] activePhrase exists=',
        !!this.phrases[this.activePhrase],
        'cursor exists=',
        !!this.phrases[this.activePhrase]?.cursor,
      );
      NotifyReactEvents.StartedPlaying();
      const cursor = this.mobileCursor ?? this.phrases[this.activePhrase]?.cursor;
      this.phrases[this.activePhrase]?.activate();
      cursor?.unpause();
      console.log('[startExercise] === RESUME END ===');
    }
  }

  // runs every 'tick'
  tickUpdate(beatDuration: number) {
    this.tempo = this.eventStream?.timeKeeper.getBpm() ?? 72;
    if (this.appTourPlaybackActive) {
      this.metronomeClick();
      return;
    }
    if (this.playbackStarted) {
      this.chevronManager?.onTick(this.tempo);
    } else {
      this.metronomeClick();
      this.mobileCursor?.blink(beatDuration);
      this.phrases[this.activePhrase]?.cursor?.blink(beatDuration);
    }
    const currentTimestamp = this.eventStream?.timeKeeper?.audioTimeToMeasureTimestamp() ?? 0;
    //this.debugLog(`[tickUpdate] currentTimestamp=${currentTimestamp}`);
    if (this.adaptiveTempoController) {
      const newTempo = this.adaptiveTempoController?.onTick(currentTimestamp);
      if (newTempo) {
        this.debugLog(`newTempo=${newTempo}`);
        //this.tempo = newTempo;
        //this.timeKeeperAuxiliary!.setBpm(newTempo);
      }
    }
  }

  update() {
    if (this.pauseQueued) {
      this._pauseExercise();
    }
    if (this.shouldUpdate) {
      this.mobileCursor?.update();
      this.phrases.forEach((p) => p?.update());

      // App tour playback: end demo when cursor reaches end of (top) phrase
      const topPhraseForTour = this.phrases[this.topPhrase];
      if (this.appTourPlaybackActive && topPhraseForTour?.cursor && this.appTourDemoIterator) {
        const topCursor = topPhraseForTour.cursor;
        if (topCursor.isDemoMode() && this.appTourDemoIterator.EndReached) {
          this.demoTimeKeeper?.pause();
          this.demoTimeKeeper?.resetTime();
          topCursor.clearDemoOverride();
          this.appTourPlaybackActive = false;
          this.appTourDemoIterator = undefined;
        }
      }

      const timeKeeper =
        this.appTourPlaybackActive && this.demoTimeKeeper
          ? this.demoTimeKeeper
          : this.playbackStarted
            ? this.eventStream?.timeKeeper
            : this.timeKeeperAuxiliary;
      if (timeKeeper === undefined) {
        Logger.warn('timekeeper unavailable!');
        return;
      }
      const timestamp = timeKeeper.audioTimeToMeasureTimestamp();
      if (this.lastTickTime === undefined) {
        this.lastTickTime = timestamp - TimeKeeper.METRONOME_SCHEDULE_AHEAD_TIME;
      }
      const beatFraction = 1 / 4;
      const measureDurationSinceLastTickTime = timestamp - this.lastTickTime;
      const bpmForBeat = this.appTourPlaybackActive ? 72 : (this.tempo ?? 72);
      if (measureDurationSinceLastTickTime > beatFraction) {
        const beatDuration = (60 / bpmForBeat) * 1000;
        this.tickUpdate(beatDuration);
        this.lastTickTime =
          this.lastTickTime === undefined
            ? undefined
            : timestamp - (timestamp - this.lastTickTime - 0.25);
      }
    }
  }

  updateGoalTempo({ goalTempo }: { goalTempo: number }) {
    console.debug('[collected message] updateGoalTempo', goalTempo);
    this.adaptiveTempoController?.setGoalTempo(goalTempo);
    this.chevronManager?.setGoalTempo(goalTempo);
  }

  showToast(message: string) {
    Logger.debug(message);
  }

  onPlayingLastSlice(start: number, duration: number, _durationInSecs: number) {
    if (!this.mobileView) {
      const nextPhrase = (this.activePhrase + 1) % 4;
      const phrase = this.phrases[nextPhrase];
      if (phrase) {
        const firstNotePos = phrase.graphic.getFirstNotePosition();
        phrase.cursor?.setX(firstNotePos - (phrase.cursor?.width ?? 0));
        phrase.activate(false, false);
        phrase.cursor?.startCountingIn(start, duration, duration);
      }
    }
  }

  onFinishPlayingPhrase(playedOnce?: boolean) {
    const eventStream = this.eventStream;
    const acc = eventStream != null ? eventStream.calcAccuracy() : 0;
    const nextPhrase = this.phrases[(this.activePhrase + 1) % 4];
    const activePhrase = this.phrases[this.activePhrase];
    //if (!this.freePlayMode) {
    this.chevronManager?.onPhraseFinishPlaying(playedOnce ?? false, nextPhrase, activePhrase);
    //}
    const nextSlot = (this.activePhrase + 1) % 4;
    if (!this.mobileView) {
      this.phrases[this.activePhrase]?.deactivate();
      this.phrases[this.activePhrase]?.activate(true);
      this.phrases[this.activePhrase]?.cursor?.setEligible(acc >= 95);
    }
    this.activePhrase = nextSlot;
  }

  onGainChevron(nextPhrase: PlayablePhrase, activePhrase: PlayablePhrase) {
    if (!isFeatureEnabled(FeatureFlag.USE_ADAPTIVE_TEMPO)) return;
    console.debug(
      '[onGainChevron]',
      this.phrases[this.activePhrase],
      activePhrase,
      nextPhrase,
      nextPhrase === activePhrase,
    );
    const timeSig = nextPhrase.graphic._data.timeSignature;
    const beatsPerMeasure = timeSig?.numerator ?? 4;
    const measureDuration = (1 / 4) * beatsPerMeasure;
    const measureStartTimestamp = nextPhrase.graphic._data.startTimestamp + measureDuration;
    const measureEndTimestamp = measureStartTimestamp + measureDuration * 2;
    if (
      activePhrase.graphic.adaptiveSegmentState ||
      !this.adaptiveTempoController?.shouldStartAdaptiveSegment(1, this.tempo)
    ) {
      return;
    }
    //this.phrases[this.activePhrase]?.graphic.boostMeasures(1, 2);
    nextPhrase.graphic.setAdaptiveSegment(1, 2, 'speed-up');
    //NotifyReact(NotifyReactEvents.AdaptiveTempoRegionCreated('speed-up'));
    NotifyReactEvents.AdaptiveTempoRegionCreated('speed-up');
    this.nextAdaptiveSegment = {
      base: this.tempo,
      delta: 5,
      startTimestamp: measureStartTimestamp,
      endTimestamp: measureEndTimestamp,
    };
  }

  onFinishAdaptiveSegment(finalTempo: number) {
    this.tempo = finalTempo;
    this.nextAdaptiveSegment = undefined;
  }

  onLoseChevron(nextPhrase: PlayablePhrase, activePhrase: PlayablePhrase) {
    if (!isFeatureEnabled(FeatureFlag.USE_ADAPTIVE_TEMPO)) return;
    console.debug(
      '[onLoseChevron]',
      this.phrases[this.activePhrase],
      activePhrase,
      nextPhrase,
      nextPhrase === activePhrase,
    );
    const timeSig = nextPhrase.graphic._data.timeSignature;
    const beatsPerMeasure = timeSig?.numerator ?? 4;
    const measureDuration = (1 / 4) * beatsPerMeasure;
    const measureStartTimestamp = nextPhrase.graphic._data.startTimestamp + measureDuration;
    const measureEndTimestamp = measureStartTimestamp + measureDuration * 2;
    if (activePhrase.graphic.adaptiveSegmentState) {
      return;
    }
    //this.phrases[this.activePhrase]?.graphic.boostMeasures(1, 2);
    if (!this.adaptiveTempoController?.shouldStartAdaptiveSegment(-1, this.tempo)) return;
    NotifyReactEvents.AdaptiveTempoRegionCreated('slow-down');
    nextPhrase.graphic.setAdaptiveSegment(1, 2, 'slow-down');
    this.nextAdaptiveSegment = {
      base: this.tempo,
      delta: -10,
      startTimestamp: measureStartTimestamp,
      endTimestamp: measureEndTimestamp,
    };
  }

  onStartPlayingMeasure(measure: number) {
    console.debug('[ata] measure=', measure, this.nextAdaptiveSegment);
    const isBottomPhrase = this.phrases[this.activePhrase]?.graphic.bottom || false;
    if (measure === 1 && this.nextAdaptiveSegment) {
      this.adaptiveTempoController?.startAdaptiveSegment(
        this.nextAdaptiveSegment.base,
        this.nextAdaptiveSegment.delta,
        this.nextAdaptiveSegment.startTimestamp,
        this.nextAdaptiveSegment.endTimestamp,
        isBottomPhrase,
      );
    } else if (measure === 3 && this.nextAdaptiveSegment) {
      //this.adaptiveTempoController?.finishAdaptiveSegment();
      //this.nextAdaptiveSegment = undefined;
    }
  }

  addPhrase(phraseData: {
    phraseRaw: Phrase;
    phrase: PhraseData;
    cursorData: PrerenderedPhraseData[];
  }) {
    console.debug('[addPhrase] adding phrase', phraseData);
    let phraseToLoad = phraseData;
    // Do not add a phrase if its starting timestamp is found to be earlier than the phrase that is
    // currently being played because it is probably a duplicate addPhrase call.
    if (
      this.phrases[this.activePhrase] &&
      phraseToLoad.phrase.startTimestamp <=
        ((this.phrases[this.activePhrase]?.cursor ?? this.mobileCursor)?.getTimestamp() ?? 0)
    ) {
      return;
    }
    // Check if we have pending phrases in the load queue that need to be loaded BEFORE the phrase that
    // is currently being requested to load (the load queue is assumed to always be sorted)
    this.phraseLoadQueue = this.phraseLoadQueue.sort(
      (a, b) => b.phrase.startTimestamp - a.phrase.startTimestamp,
    );
    const inQueue = this.phraseLoadQueue.at(0);
    if (inQueue && inQueue.phrase.startTimestamp < phraseToLoad.phrase.startTimestamp) {
      phraseToLoad = inQueue;
      this.phraseLoadQueue[0] = phraseData;
    }
    const { phrase, phraseRaw, cursorData } = phraseToLoad;
    const seenTs = this.phrasesSeen.get(phrase.uuid);
    if (phraseToLoad && seenTs !== phrase.startTimestamp) {
      // Extract first VexFlow ID from incoming data for staleness check
      const incomingFirstVfId =
        cursorData[0]?.currentVoiceEntries?.[0]?.Notes?.[0]?.graphics?.[0]?.staveNote?.id;
      const incomingAllVfIds = cursorData
        .flatMap((s) =>
          s.currentVoiceEntries.flatMap((v) => v.Notes.map((n) => n.graphics?.[0]?.staveNote?.id)),
        )
        .filter(Boolean);

      Logger.debug(
        `[addPhrase] Adding phrase uuid=${phrase.uuid} startTimestamp=${phrase.startTimestamp} currentIdx=${this.currentIdx} activePhrase=${this.activePhrase} phrases.length=${this.phrases.length}`,
      );
      Logger.debug(
        `[addPhrase] Incoming VexFlow IDs: first=${incomingFirstVfId} total=${
          incomingAllVfIds.length
        } all=[${incomingAllVfIds.join(',')}]`,
      );
      // Check for VexFlow ID collisions with existing phrases
      this.phrases.forEach((p, i) => {
        if (!p || p.graphic.isDestroyed) return;
        const existingVfIds = new Set<string>();
        p.graphic.trebleClef?.notes.forEach((n) => existingVfIds.add(n.id));
        p.graphic.bassClef?.notes.forEach((n) => existingVfIds.add(n.id));
        const collisions = incomingAllVfIds.filter((id) => existingVfIds.has(id));
        if (collisions.length > 0) {
          Logger.warn(
            `[addPhrase] VF ID COLLISION with slot ${i} (uuid=${p.graphic._data.uuid} ts=${
              p.graphic._data.startTimestamp
            }): ${collisions.length} shared IDs: [${collisions.slice(0, 5).join(',')}...]`,
          );
        }
      });
      Logger.debug(
        `[addPhrase] phraseLoadQueue:`,
        this.phraseLoadQueue.map((q) => `uuid=${q.phrase.uuid} ts=${q.phrase.startTimestamp}`),
      );
      Logger.debug(
        `[addPhrase] Current phrase slots:`,
        this.phrases.map((p, i) =>
          p
            ? `[${i}] uuid=${p.graphic._data.uuid} ts=${p.graphic._data.startTimestamp} active=${p.isActive} destroyed=${p.graphic.isDestroyed}`
            : `[${i}] empty`,
        ),
      );
      if (phrase.startTimestamp !== 0 && this.phrases.length === 0) {
        Logger.debug(`[addPhrase] Queuing phrase (no phrases loaded yet, startTimestamp != 0)`);
        this.phraseLoadQueue.push({ phrase, cursorData, phraseRaw });
        return;
      }
      if (this.currentIdx) {
        const cursor = this.mobileCursor ?? this.phrases[this.activePhrase]?.cursor;
        const nextPhrase = this.phrases[(this.currentIdx + 3) % 4]?.graphic._data;
        if (this.phrases[this.activePhrase]) {
          const firstPhrasePlaying =
            this.phrases.length == 2 &&
            cursor!.getTimestamp() < getPhraseEnd(this.phrases[0]!.graphic._data);
          if (
            firstPhrasePlaying ||
            (nextPhrase && cursor!.getTimestamp() < getPhraseEnd(nextPhrase))
          ) {
            Logger.debug(`[addPhrase] Queuing phrase (active phrase still playing)`);
            this.phraseLoadQueue.push({ phrase, cursorData, phraseRaw });
            return;
          } else if (this.phrases[this.activePhrase] && nextPhrase) {
            // intentionally empty
          }
        }
      }
      const x = this.mobileView
        ? this.currentIdx !== undefined
          ? this.phrases[this.currentIdx]!.graphic.getNextPosition()
          : 100
        : 100;
      const _idx = this.currentIdx ?? 0;
      if (_idx % 2 === 0) {
        this.topPhrase = _idx;
      } else {
        this.bottomPhrase = _idx;
      }
      this.currentIdx = this.currentIdx !== undefined ? (this.currentIdx + 1) % 4 : 0;
      phrase.showClef = !this.mobileView || phrase.startTimestamp === 0;
      phrase.showBrace = !this.mobileView || phrase.startTimestamp === 0;
      phrase.showTimeSig = phrase.startTimestamp === 0;
      const phraseGraphic = new PhraseGraphic(
        this,
        x,
        {
          globalScaling: 1,
          pitchLineGap: 20,
          pitchLineWidth: 1 * window.devicePixelRatio,
          measureLineWidth: 2 * window.devicePixelRatio,
          clefGap: 100,
          doubleEndLines: false,
        },
        phrase,
        'Fade',
        this.measureCount,
        undefined,
        this.currentIdx % 2 === 1,
      );
      //phraseGraphic.setAdaptiveSegment(1, 2, 'slow-down');
      this.measureCount += phrase.measureData.length; // Hack/Jank: This is hardcoded for now, maybe look into making it dynamic
      const idx = this.currentIdx;
      const outgoingSlot = (idx + 2) % 4;
      const outgoingPhrase = this.phrases[outgoingSlot];
      Logger.debug(
        `[addPhrase] New phrase placed at slot ${idx}, outgoingSlot=${outgoingSlot}, outgoing exists=${!!outgoingPhrase}, bottom=${
          this.currentIdx % 2 === 1
        }`,
      );
      outgoingPhrase?.graphic.transitionOut(() => {
        Logger.debug(
          `[transitionOut callback] outgoingSlot=${outgoingSlot}, still same phrase=${
            this.phrases[outgoingSlot] === outgoingPhrase
          }, fading in slot=${idx ?? 0}`,
        );
        if (this.phrases[outgoingSlot] === outgoingPhrase) {
          this.phrases[outgoingSlot]?.destroy();
          this.phrases[outgoingSlot] = undefined;
        }
        this.phrases[idx ?? 0]?.graphic.transition('Fade');
      }, this);
      const cameraHeight = this.cameras.main.height;
      const phraseGap = 50 * window.devicePixelRatio;
      const availVertSpace = phraseGraphic.getBoundingBoxHeight() * 2 + phraseGap;
      const paddingVert = (cameraHeight - availVertSpace) / 2;
      const phraseY = !this.mobileView
        ? paddingVert +
          (this.currentIdx % 2 === 1 ? phraseGraphic.getBoundingBoxHeight() + phraseGap : 0)
        : //: this.cameras.main.height / 2 - phraseGraphic.getBoundingBoxHeight() / 2;
          0;
      phraseGraphic.setPosition(x, phraseY);
      if (this.mobileView) {
        const maxProps: SizingProps = {
          minPaddingX: 125,
          minPaddingY: 30,
          idealPhraseConfig: DefaultConfig({}),
          minPhraseConfig: MediumConfig(),
          minStaffSpacingVertical: 35,
          idealPaddingX: 150,
          idealPaddingY: 75,
        };
        const minProps: SizingProps = {
          minPaddingX: 30,
          minPaddingY: 10,
          idealPhraseConfig: maxProps.minPhraseConfig,
          minPhraseConfig: MinimumConfig(),
          idealPaddingX: maxProps.minPaddingX,
          idealPaddingY: maxProps.minPaddingY,
          minStaffSpacingVertical: 0,
        };
        const { paddingY, phraseDimens, phraseRenderingConfig } = optimizeStaffLineHeight(
          [this.width, this.height],
          { min: minProps, max: maxProps },
          4.5,
          8,
          this.mobileView,
        );
        const { topClefNumExtraLinesTop, innerHeight } = phraseDimens;
        const yOffset = topClefNumExtraLinesTop * 0.5 * phraseRenderingConfig.pitchLineGap;
        const phraseY = paddingY + yOffset;
        const width = innerHeight * 4.5;
        console.debug('[addPhrase] resizing phraseGraphic to width=', width, 'x=', x);
        phraseGraphic.resize(
          phraseRenderingConfig,
          width,
          //config.paddingX,
          //config.paddingY,
          0,
          0,
          phraseDimens,
          innerHeight,
        );
        console.debug('[addPhrase] size=', {
          height: this.height,
          width: this.width,
          phraseY,
          paddingY,
          innerHeight,
        });
        // Vertically center the phrase using its bounding box height and camera's view height,
        // since phraseDimens doesn't account for devicePixelRatio (DPR).
        const camHeight = this.cameras.main.height;
        // Compute y so that the phrase's bounding box is vertically centered in the viewport
        const centeredY = (camHeight - innerHeight * window.devicePixelRatio) / 2;
        console.debug(
          '[center phrase] camHeight=',
          camHeight,
          'bbHeight=',
          innerHeight * window.devicePixelRatio,
          'centeredY=',
          centeredY,
        );
        phraseGraphic.setY(centeredY);
        if (!this.originMarker) {
          this.originMarker = this.add.rectangle(0, 0, 40, 40, 0xff0000, 0.1);
          this.originMarker.setOrigin(0, 0);
        }
        this.originMarker.setPosition(x, 0);
        phraseGraphic.setX(x);
      }
      const cursorHeight = phraseGraphic.getInnerHeight() * 1.35;
      const cursorWidth = cursorHeight / 10;
      const cursorY = phraseGraphic.getInnerHeight() / 2 - cursorHeight / 2 + phraseGraphic.y;
      const firstNotePosition = phraseGraphic.getFirstNotePosition(0);
      let phraseCursor = this.mobileCursor;
      if (!this.mobileView || phraseCursor == undefined) {
        phraseCursor = new CursorMobile(
          this,
          firstNotePosition,
          cursorY,
          cursorWidth,
          cursorHeight,
          this.eventStream!,
          new IteratorExtensible(
            this,
            [],
            phraseRaw,
            phrase.timeSignature,
            undefined,
            undefined,
            this.totalPhrases,
          ),
          phraseData.cursorData,
          this.mobileView,
          //false,
          this.onPlayingLastSlice,
          this.onFinishPlayingPhrase,
          this,
          this.phrasesSeen.size > 0,
          phrase.startTimestamp,
          this.currentIdx,
          this.phrases.length === 0,
          this.onStartPlayingMeasure,
          this,
        );
        phraseCursor.reset(
          new IteratorExtensible(
            this,
            [],
            phraseRaw,
            phrase.timeSignature,
            undefined,
            undefined,
            this.totalPhrases,
          ),
          phrase.startTimestamp,
          phraseGraphic.phraseStartingMeasure,
        );
        if (this.mobileView) this.mobileCursor = phraseCursor;
      }
      if (this.cursorHidden) {
        phraseCursor?.setRenderHidden(true);
      }
      if (this.phrases.length === 0) {
        this.phrases[0] = new PlayablePhrase(
          phraseGraphic,
          this.mobileView ? undefined : phraseCursor,
        );
      } else {
        const idx = this.currentIdx;
        if (this.phrases.length >= 4 && this.currentIdx !== undefined) {
          const destroyedPhrase = this.phrases[idx];
          if (destroyedPhrase) {
            this.errorFeedbackManager?.cleanupPhrase(destroyedPhrase.graphic._data.uuid);
          }
          this.phrases[idx]?.destroy();
        }
        this.phrases[this.currentIdx] = new PlayablePhrase(
          phraseGraphic,
          this.mobileView ? undefined : phraseCursor,
        );
      }
      if (!this.mobileView) this.resize({ width: this.width, height: this.height });
      phraseCursor?.addPhrase(cursorData, phraseRaw.uuid, phrase.timeSignature, this.totalPhrases);
      this.eventStream?.addEvents2(
        new Iterator(cursorData, phraseRaw),
        phraseRaw.getStartTimestamp(),
      );
      this.phrasesSeen.set(phrase.uuid, phrase.startTimestamp);
      this.phraseRawByUuid.set(phrase.uuid, phraseRaw);
      this.firstPhrase = false;
      this.totalPhrases++;
    } else {
      // intentionally empty
    }
  }

  getNoteById(id: string, phraseStart?: number): IPlayable | null {
    const note = this.phrases.map((phrase) => ({
      note: phrase?.graphic.getNoteById(id),
      phraseStart,
    }));
    note.forEach((n) => {
      if (n.note !== undefined && n.phraseStart !== phraseStart) {
        // intentionally empty
      }
    });
    return note.map((n) => n.note).find((n) => n !== undefined) ?? null;
  }

  // The update function might still be running when the Puase callback is triggered by
  // the registered event which will mess up the validity of the state.
  // Therefore, we queue a pause request which then immediately gets processed
  // in the next update() tick.
  pauseExercise() {
    this.pauseQueued = true;
  }

  _pauseExercise() {
    this.shouldUpdate = false;
    this.lastTickTime = undefined;
    this.phrases[this.activePhrase]?.pause();
    this.mobileCursor?.pause();
    const activePhrase = this.phrases[this.activePhrase];
    const cursor = activePhrase?.cursor ?? this.mobileCursor;
    const lastPlayedMeasure = cursor ? Math.max(cursor.getLastPlayedMeasure() - 1, 0) : 0;
    console.debug('[pauseExercise] lastPlayedMeasure=', lastPlayedMeasure);
    NotifyReactEvents.ExercisePaused({ lastPlayedMeasure });
    this.pauseQueued = false;
    this.setCursor({});
  }

  getCurrentPhrase() {
    return this.phrases.find((phrase) => phrase?.isActive)?.graphic ?? null;
  }

  setNoteState({
    id,
    state,
    colorblindType,
    noteTimestamp,
  }: {
    id: string;
    state: ProgressState;
    colorblindType?: COLORBLIND_TYPE | null;
    noteTimestamp?: number;
  }) {
    const note = this.getNoteByIdAndTimestamp(id, noteTimestamp);
    if (!note) {
      Logger.warn(
        `[setNoteState] id=${id} state=${state} noteTimestamp=${noteTimestamp} NOT FOUND in any phrase`,
      );
    }
    note?.setTimingState(state, colorblindType);
  }

  pulseNote({
    id,
    colorblindType,
    noteTimestamp,
  }: {
    id: string;
    colorblindType?: COLORBLIND_TYPE | null;
    noteTimestamp?: number;
  }) {
    this.getNoteByIdAndTimestamp(id, noteTimestamp)?.pulse(colorblindType);
  }

  getNoteByIdAndTimestamp(id: string, timestamp?: number): IPlayable | null {
    for (let i = 0; i < this.phrases.length; i++) {
      const phrase = this.phrases[i];
      if (!phrase) continue;
      const note = phrase.graphic.getNoteById(id, timestamp);
      if (note != null) {
        return note as IPlayable;
      }
    }
    // Log diagnostic info when note is NOT found
    console.warn(`[getNoteByIdAndTimestamp] NOT FOUND id=${id} ts=${timestamp}. Active slots:`);
    for (let i = 0; i < this.phrases.length; i++) {
      const phrase = this.phrases[i];
      if (!phrase) {
        console.warn(`  slot ${i}: empty`);
        continue;
      }
      const noteCount =
        (phrase.graphic.trebleClef?.notes.size ?? 0) + (phrase.graphic.bassClef?.notes.size ?? 0);
      const sampleIds: string[] = [];
      phrase.graphic.trebleClef?.notes.forEach((n) => {
        if (sampleIds.length < 3) sampleIds.push(n.id);
      });
      phrase.graphic.bassClef?.notes.forEach((n) => {
        if (sampleIds.length < 5) sampleIds.push(n.id);
      });
      console.warn(
        `  slot ${i}: uuid=${phrase.graphic._data.uuid} startTs=${
          phrase.graphic._data.startTimestamp
        } destroyed=${
          phrase.graphic.isDestroyed
        } noteCount=${noteCount} sampleIds=[${sampleIds.join(',')}]`,
      );
    }
    return null;
  }

  getNotePosition(id: string, timestamp?: number, rest?: boolean) {
    const pos =
      this.phrases
        .map((p) => {
          return p?.graphic.getNotePosition(id, timestamp, rest);
        })
        .find((p) => p !== undefined) ?? null;
    if (pos !== null) {
      return pos.x;
    }
    return null;
  }

  getNoteFullPosition(id: string, timestamp?: number, rest?: boolean) {
    return (
      this.phrases
        .map((p) => {
          return p?.graphic.getNotePosition(id, timestamp, rest);
        })
        .find((p) => p !== undefined) ?? null
    );
  }

  getPhraseEndPosition() {
    return Math.max(
      ...this.phrases
        .filter((p) => p !== undefined)
        .map((phrase) => phrase.graphic.getNextPosition()),
    );
  }

  setNoteColor({ color, id }: { color: string | number; id?: string }) {
    console.log(`[setNoteColor] color=${color} id=${id}`);
    if (id === undefined) {
      const phrase = this.getCurrentPhrase() ?? this.phrases.find((p) => p !== undefined)?.graphic;
      const firstNoteId = phrase?.getFirstNoteId();
      if (firstNoteId) phrase?.getNoteById(firstNoteId)?.setColor(color);
    } else {
      this.getNoteById(id)?.setColor(color);
    }
  }

  resetNoteColor({ id }: { id?: string }) {
    if (id === undefined) {
      const phrase = this.getCurrentPhrase() ?? this.phrases.find((p) => p !== undefined)?.graphic;
      const firstNoteId = phrase?.getFirstNoteId();
      if (firstNoteId) phrase?.getNoteById(firstNoteId)?.resetColor();
    } else {
      //this.getNoteById(id)?.resetColor();
    }
  }

  /**
   * Get the current cursor position and active phrase.
   * Used by error feedback system to spawn visuals at cursor location.
   */
  getCursorPosition(): { x: number; phrase: PlayablePhrase } | null {
    const activePhrase = this.phrases[this.activePhrase];
    if (!activePhrase || !activePhrase.cursor) {
      return null;
    }
    return {
      x: activePhrase.cursor.x,
      phrase: activePhrase,
    };
  }

  startPlaying() {
    this.timeKeeperAuxiliary?.pause();
    this.timeKeeperAuxiliary?.resetTime();
    this.phrases[0]?.activate();
    this.mobileCursor?.enable();
    this.phrases[0]?.cursor?.unpause(0);
    NotifyReactEvents.StartedPlaying();
    this.playbackStarted = true;
  }

  noteOn({ note, velocity }: { note: number; velocity: number }) {
    if (this.playbackStarted && !this.gameplayStarted) {
      this.eventsQueue.push([note, velocity]);
    }
    if (!this.playbackStarted && this.eventStream !== undefined) {
      const timestamp = this.timeKeeperAuxiliary!.audioTimeToMeasureTimestamp();
      if (!this.lastTickTime) {
        return;
      }
      const timeDiff = timestamp - this.lastTickTime;
      if (timeDiff > 0.06 && timeDiff < 0.125) {
        //this.showToast(`Too Late!`);
        NotifyReactEvents.ShowToast('Too late!');
      } else if (timeDiff >= 0.125 && timeDiff < 0.19) {
        //this.showToast(`Too early!`);
        NotifyReactEvents.ShowToast('Too early!');
      } else {
        this.timeKeeperAuxiliary?.pause();
        this.timeKeeperAuxiliary?.resetTime();
        const measureOffset = timeDiff > 0.125 ? timeDiff - 0.25 : timeDiff;
        this.phrases[0]?.activate();
        const cursor = this.mobileCursor ?? this.phrases[0]?.cursor;
        cursor?.enable();
        cursor?.unpause(measureOffset);
        this.eventsQueue.push([note, velocity]);
        NotifyReactEvents.StartedPlaying({
          measureOffset,
          processEventsQueue: [this.processEventsQueue, this],
        });
        this.playbackStarted = true;
      }
    }
  }

  processEventsQueue() {
    this.eventsQueue.forEach(([note, velocity]) => {
      if (velocity === 0) this.eventStream?.handleNoteOffEvent(note);
      else this.eventStream?.handleNoteOnEvent(note, velocity);
    });
    this.eventsQueue = [];
  }

  noteOff({ note: _note }: { note: number }) {}

  destroy() {
    // Clean up error feedback for all phrases
    this.errorFeedbackManager?.cleanupAll();
    this.phrases.forEach((p) => p?.destroy());
    this.phrases.clear();
    RemoveListener('SetCursor');
    RemoveListener('UpdateGoalTempo');
    RemoveListener('AddPhraseLegacy');
    RemoveListener('ResizeWindow');
    RemoveListener('SetFreePlayMode');
    RemoveListener('SetZoomLevel');
    RemoveListener('PauseExercise');
    RemoveListener('StartExercise');
    RemoveListener('ChangeTempo');
    RemoveListener('SetNoteState');
    RemoveListener('PulseNote');
    RemoveListener('SetCursorVisible');
    RemoveListener('UnloadExercise');
    RemoveListener('MidiOn');
    RemoveListener('MidiOff');
    RemoveListener('SetFreePlayMode');
    RemoveListener('ResetChevrons');
    RemoveListener('SetNoteColor');
    RemoveListener('ResetNoteColor');
    RemoveListener('SetAppTourStep');
    RemoveListener('EndTour');
    this.playbackStarted = false;
    this.shouldUpdate = false;
    this.pauseQueued = false;
  }

  resize(props: { width: number; height: number }) {
    this.transformPointToScreen(0, 0);
    const width = props.width;
    const height = props.height;
    this.width = width;
    this.height = height;
    this.scale.resize(width * window.devicePixelRatio, height * window.devicePixelRatio);
    if (this.mobileView) return;
    const hasAnyPhrase = this.phrases.some((p) => p !== undefined);
    if (!hasAnyPhrase) return;
    const minAspectRatio = 4.5;
    const maxAspectRatio = 8;
    const maxProps: SizingProps = {
      minPaddingX: 125,
      minPaddingY: 50,
      idealPhraseConfig: DefaultConfig({}),
      minPhraseConfig: MediumConfig(),
      minStaffSpacingVertical: 35,
      idealPaddingX: 150,
      idealPaddingY: 75,
    };
    const minProps: SizingProps = {
      minPaddingX: 125,
      minPaddingY: 30,
      idealPhraseConfig: maxProps.minPhraseConfig,
      minPhraseConfig: MinimumConfig(),
      idealPaddingX: maxProps.minPaddingX,
      idealPaddingY: maxProps.minPaddingY,
      minStaffSpacingVertical: 20,
    };
    const { paddingX, paddingY, phraseRenderingConfig, staffSpacingVertical, phraseDimens, fit } =
      optimizeStaffLineHeight(
        [width, height],
        { min: minProps, max: maxProps },
        minAspectRatio,
        maxAspectRatio,
        this.mobileView,
      );
    if (!fit)
      Logger.warn(
        '[SightReadingPhaser::resize] not enough space for staff lines! Falling back to minimum configuration',
      );
    const { topClefNumExtraLinesTop, innerHeight } = phraseDimens;
    const minWidth = minAspectRatio * innerHeight;
    const availableWidth = Math.max(minWidth, width - paddingX * 2);
    const braceWidth = getBraceWidth(phraseDimens, this) * window.devicePixelRatio;
    this.updateMaxZoomLevel(
      paddingX * window.devicePixelRatio,
      paddingY * window.devicePixelRatio,
      braceWidth,
    );
    /*Logger.debug(
      `[SightReadingPhaser::resize] Resizing ${this.phrases.filter((p) => p !== undefined).length} phrases. Slots:`,
      this.phrases.map((p, i) =>
        p
          ? `[${i}] uuid=${p.graphic._data.uuid} bottom=${p.graphic.bottom} destroyed=${p.graphic.isDestroyed}`
          : `[${i}] empty`,
      ),
    );*/
    this.phrases.forEach((p, slotIdx) => {
      if (p === undefined) return;
      const phraseHeightTotal =
        phraseDimens.topClefHeight.outerHeight +
        phraseDimens.bottomClefHeight.outerHeight +
        phraseRenderingConfig.clefGap +
        1 * phraseRenderingConfig.pitchLineGap;
      const yOffset = topClefNumExtraLinesTop * 0.5 * phraseRenderingConfig.pitchLineGap;
      const phraseY =
        paddingY + yOffset + (p.graphic.bottom ? phraseHeightTotal + staffSpacingVertical : 0);
      p.graphic.resize(
        phraseRenderingConfig,
        availableWidth,
        paddingX,
        phraseY,
        phraseDimens,
        innerHeight,
      );
      const c = p?.cursor;
      if (c === undefined) return;
      c.height = innerHeight * 1.25 * window.devicePixelRatio;
      c.width = c.height / 10;
      c.radius = c.width / 2;
      const y = (phraseY + innerHeight / 2) * window.devicePixelRatio - c.height / 2;
      c.setY(y);
      if (!this.playbackStarted && c.idx === 0) {
        const firstNotePhrase =
          this.phrases[this.activePhrase] ?? this.phrases.find((ph) => ph !== undefined);
        const firstNote = firstNotePhrase?.graphic.getFirstNotePosition();
        if (firstNote !== undefined) {
          c.setLastNotePos(firstNote - c.width / 2);
          c.setX(firstNote - c.width / 2);
        }
      }
      if (p.graphic.bottom) {
        this.phrase2Gizmo?.setSize(
          availableWidth * window.devicePixelRatio,
          phraseHeightTotal * window.devicePixelRatio,
        );
        this.phrase2Gizmo?.setX(paddingX * window.devicePixelRatio);
        this.phrase2Gizmo?.setY((phraseY - yOffset) * window.devicePixelRatio);
      } else {
        this.phrase1Gizmo?.setY((phraseY - yOffset) * window.devicePixelRatio);
        this.phrase1Gizmo?.setX(paddingX * window.devicePixelRatio);
        this.phrase1Gizmo?.setSize(
          availableWidth * window.devicePixelRatio,
          phraseHeightTotal * window.devicePixelRatio,
        );
      }
    });
  }

  /**
   * Get note IDs in order by vertical slice (notes only, no rests).
   * One ID per slice: the first non-rest note in that slice.
   */
  private getNoteIdsBySlice(phrase: PlayablePhrase): string[] {
    const data = phrase.graphic._data;
    const ids: string[] = [];
    for (const slice of data.serializedIterator) {
      let found = false;
      for (const entry of slice.currentVoiceEntries) {
        for (const note of entry.Notes) {
          if (note.isRest || !note.printObject) continue;
          const id = note.graphics?.[0]?.staveNote?.id;
          if (id) {
            ids.push(id);
            found = true;
            break;
          }
        }
        if (found) break;
      }
    }
    return ids;
  }

  private onAdaptiveTempoDone(finalTempo: number) {
    this.phrases[this.activePhrase]?.graphic.doneAdaptiveSegment();
    this.onFinishAdaptiveSegment(finalTempo);
  }

  setAppTourStep({ step, cbType }: { step: AppTourStepPhaser; cbType: COLORBLIND_TYPE | null }) {
    if (!this.isTourActive) {
      const topPhrase = this.phrases[this.topPhrase];
      if (topPhrase?.cursor) this.savedCursorPosition = topPhrase.cursor.x;
      console.debug('[tour] setAppTourStep', topPhrase, this.savedCursorPosition);
      //topPhrase.cursor.savePosition();
      this.isTourActive = true;
    }
    this.appTourStep = step;

    if (step === 'playback') {
      //this.startAppTourPlayback();
      return;
    }
    if (step === 'perfect-notes' || step === 'off-time-notes' || step === 'error-notes') {
      this.applyAppTourColoringStep(step, cbType);
      return;
    }
  }

  private restoreCursor() {
    const phrase = this.phrases[this.topPhrase];
    if (!phrase?.cursor) return;
    //phrase.cursor.setX(phrase.cursor.lastNotePos);
    //phrase.cursor.setY(phrase.cursor.y);

    //phrase.cursor.setWidth(phrase.cursor.width);
    //phrase.cursor.setHeight(phrase.cursor.height);
  }

  private startAppTourPlayback() {
    const top = this.phrases[this.topPhrase];
    if (!top?.cursor) {
      Logger.warn('[AppTour] Playback step requires at least the top phrase on screen');
      return;
    }

    // 1. Save game state for restore on EndTour
    this.savedTourState = {
      activePhrase: this.activePhrase,
      timeKeeperPaused: this.eventStream?.timeKeeper?.isPaused() ?? true,
      lastTickTime: this.lastTickTime,
    };

    // 2. Normalize top phrase cursor data so first slice has currentTimestamp 0 (align with metronome)
    const topCursorData = top.cursor.cursorData;
    const firstTimestamp = topCursorData.length > 0 ? topCursorData[0].currentTimestamp : 0;
    const normalizedCursorData = topCursorData
      .map((item) => ({
        ...item,
        currentVoiceEntries: item.currentVoiceEntries.filter(
          (e) => e.Notes.filter((n) => n.printObject).length > 0,
        ),
        currentTimestamp: item.currentTimestamp - firstTimestamp,
      }))
      .filter((s) => s.currentVoiceEntries.length > 0);

    // 3. Demo TimeKeeper at 72 BPM; startMeasure 0 so timestamp 0 = first beat (cursor and clicks in sync)
    this.demoTimeKeeper = new TimeKeeper();
    const timeSig = top.graphic._data.timeSignature;
    this.demoTimeKeeper.setTimeSignatures(0, [timeSig]);
    this.demoTimeKeeper.setBpm(72);
    this.demoTimeKeeper.resetTime();
    Tone.start();
    this.demoTimeKeeper.unpause(72, 0, 0, NoteType._32nd);

    // 4. Demo iterator (top phrase only, timestamps start at 0)
    const topPhraseRaw = this.phraseRawByUuid.get(top.graphic._data.uuid);
    if (!topPhraseRaw) {
      Logger.warn('[AppTour] Could not get phraseRaw for top phrase');
      return;
    }
    const demoIterator = new IteratorExtensible(
      this,
      normalizedCursorData,
      topPhraseRaw,
      timeSig,
      0,
    );
    this.appTourDemoIterator = demoIterator;

    // 5–6. Snap cursor to first note, attach demo (cursor uses existing update path driven by demo timekeeper)
    const firstNotePos = top.graphic.getFirstNotePosition();
    top.activate(false, true);
    top.cursor.setDemoOverride(this.demoTimeKeeper, demoIterator);
    top.cursor.setDemoStartPosition(firstNotePos);
    top.cursor.unpause();
    this.appTourPlaybackActive = true;
    if (!this.shouldUpdate) this.shouldUpdate = true;
  }

  private transformPointToScreen(x: number, y: number) {
    // There is probably a much better way of doing this already in Phaser, but this works for now
    const cam = this.cameras.main;
    const zoom = cam.zoom;
    const width = cam.width;
    const height = cam.height;
    const halfWidth = width / 2;
    const halfHeight = height / 2;
    const xTransformed = (x - halfWidth) * zoom;
    const yTransformed = (y - halfHeight) * zoom;
    const xTransformedTranslated = (xTransformed + halfWidth) / window.devicePixelRatio;
    const yTransformedTranslated = (yTransformed + halfHeight) / window.devicePixelRatio;
    return { x: xTransformedTranslated, y: yTransformedTranslated };
  }

  private applyAppTourColoringStep(
    step: 'perfect-notes' | 'off-time-notes' | 'error-notes',
    cbType: COLORBLIND_TYPE | null,
  ) {
    const phrase = this.phrases[this.topPhrase];
    if (!phrase?.cursor || !phrase.graphic) return;
    phrase.cursor.clearDemoOverride();
    //if (step === 'perfect-notes') phrase.cursor.savePosition();
    if (step === 'perfect-notes') phrase.cursor.moveTo(this.savedCursorPosition ?? 0, true);
    if (this.appTourNoteIdsBySlice.length === 0) {
      this.appTourNoteIdsBySlice = this.getNoteIdsBySlice(phrase);
    }
    const noteIds = this.appTourNoteIdsBySlice;
    if (noteIds.length === 0) return;

    const stepIndex = step === 'perfect-notes' ? 0 : step === 'off-time-notes' ? 1 : 2;
    const prevStepIndex = this.appTourColoringStepIndex;
    this.appTourColoringStepIndex = stepIndex;

    // When going back a step, restore current note only
    if (stepIndex < prevStepIndex && prevStepIndex < noteIds.length) {
      const currentNoteId = noteIds[prevStepIndex];
      const note = this.getNoteById(currentNoteId) as NoteGraphic | null;
      note?.restoreFromDemo?.();
      this.appTourDemoTouchedNoteIds.delete(currentNoteId);
    }

    // Cursor to first note of first phrase
    //const firstNotePos = phrase.graphic.getFirstNotePosition();
    //phrase.cursor.targetNoteSmooth(firstNotePos - phrase.cursor.width / 2);
    //phrase.cursor.setX(firstNotePos - (phrase.cursor.width / 2));
    phrase.activate(false, true);

    const targetNoteId = noteIds[stepIndex];
    if (!targetNoteId) return;
    const note = this.getNoteById(targetNoteId) as NoteGraphic | null;
    if (!note) return;

    const targetPos = this.getNoteFullPosition(targetNoteId);
    if (targetPos !== null) {
      /*if (this.notePosGizmo) {
        this.notePosGizmo.setPosition(targetPos.x, targetPos.y);
      } else
        this.notePosGizmo = this.add.rectangle(targetPos.x, targetPos.y, 20, 20, 0x0000ff, 0.4);
      this.notePosGizmo.setOrigin(0, 0);*/
      const targetPosTransformed = this.transformPointToScreen(targetPos.x, targetPos.y);
      NotifyReactEvents.SetNotePosDummy({ x: targetPosTransformed.x, y: targetPosTransformed.y });
    }

    const targetX = this.getNotePosition(targetNoteId);
    if (targetX != null) {
      const cursor = phrase.cursor;
      const targetCursorCenter = targetX;
      /*const targetCursorLeft = targetCursorCenter - cursor.width / 2;
      this.tweens.add({
        targets: cursor,
        x: targetCursorLeft,
        duration: 300,
        ease: 'Power2.Out',
      });*/
      //cursor.setLastNotePos(targetCursorCenter);
      cursor.targetNoteSmooth(targetCursorCenter);
      //cursor.moveTo(targetCursorCenter);
    }

    const state =
      step === 'perfect-notes'
        ? ProgressState.Ended
        : step === 'off-time-notes'
          ? ProgressState.LateStart
          : ProgressState.Missed;
    note.setDemoTimingState?.(state, cbType);
    this.appTourDemoTouchedNoteIds.add(targetNoteId);
  }

  endTour() {
    this.isTourActive = false;

    console.debug('[tour] endTour', this.savedCursorPosition);
    const topPhrase = this.phrases[this.topPhrase];
    //topPhrase?.cursor?.targetNoteSmooth(this.savedCursorPosition ?? 0);
    if (this.savedCursorPosition !== undefined) {
      //topPhrase?.cursor?.movementTweenHandle?.stop();
      topPhrase?.cursor?.moveTo(this.savedCursorPosition, true);
    }
    //topPhrase?.cursor?.targetNoteSmooth(this.savedCursorPosition ?? 0);
    this.savedCursorPosition = undefined;

    if (topPhrase?.cursor) topPhrase.cursor.restorePosition();

    if (this.appTourPlaybackActive && topPhrase?.cursor) {
      topPhrase.cursor.clearDemoOverride();
      topPhrase.cursor.pause();
      this.demoTimeKeeper?.pause();
      this.demoTimeKeeper?.resetTime();
      this.appTourPlaybackActive = false;
      topPhrase.deactivate();
    }
    this.demoTimeKeeper = undefined;
    this.appTourDemoIterator = undefined;

    for (const noteId of this.appTourDemoTouchedNoteIds) {
      const note = this.getNoteById(noteId) as NoteGraphic | null;
      if (note?.restoreFromDemo) {
        note.restoreFromDemo();
      }
    }
    this.appTourDemoTouchedNoteIds.clear();

    if (this.savedTourState) {
      this.activePhrase = this.savedTourState.activePhrase;
      this.lastTickTime = this.savedTourState.lastTickTime;
      if (this.eventStream?.timeKeeper && this.savedTourState.timeKeeperPaused) {
        this.eventStream.timeKeeper.pause();
      }
      const restoredPhrase = this.phrases[this.activePhrase];
      if (restoredPhrase) {
        restoredPhrase.activate(false, true);
      }
      this.savedTourState = undefined;
    }

    this.appTourStep = null;
    this.appTourNoteIdsBySlice = [];
    this.appTourColoringStepIndex = 0;
  }
}
